/**
 * BuildingRenovationLabels
 *
 * De renovatielabels van een gebouw, opgesomd onder de gebouwcode bovenaan de
 * renovatietab: waar gaat de renovatie van dit pand over?
 *
 * Elk projectonderdeel heeft een categorie (dak, elektriciteit, sanitair, …).
 * Die stonden verspreid over de projectkaarten en de onderdelenlijsten, dus om te
 * weten waarover een renovatie gaat moest je elk project openklikken. Hier staat
 * het in één regel.
 *
 * De labels komen uit gegevens die de pagina al heeft (de projecten met hun
 * onderdelen) — geen extra API-call. Kleur en icoon komen van dezelfde helper als
 * de renovatietickets, zodat "Dak" hier hetzelfde chipje is als daar.
 */

import { useMemo } from "react";
import { Tag } from "@phosphor-icons/react";
import { getRenoCategoryStyle } from "screens/renoHelpdesk/utils/renoCategoryColor";
import type { RenovationProject } from "shared/types/renovation";

interface BuildingRenovationLabelsProps {
  projects: RenovationProject[];
  /** true zolang de projecten nog geladen worden. */
  isLoading?: boolean;
}

interface LabelTelling {
  naam: string;
  /** Aantal projectonderdelen met deze categorie. */
  onderdelen: number;
}

/**
 * Labels van alle projectonderdelen, ontdubbeld en gesorteerd: het meest
 * voorkomende eerst, bij gelijk aantal alfabetisch. Zo staat vooraan waar het
 * meeste werk in zit.
 */
export const telRenovatieLabels = (
  projects: RenovationProject[],
): LabelTelling[] => {
  const perNaam = new Map<string, LabelTelling>();

  for (const project of projects ?? []) {
    for (const part of project?.parts ?? []) {
      const naam = (part as any)?.label?.name?.trim();
      if (!naam) continue;
      const bestaand = perNaam.get(naam);
      if (bestaand) bestaand.onderdelen += 1;
      else perNaam.set(naam, { naam, onderdelen: 1 });
    }
  }

  return Array.from(perNaam.values()).sort(
    (a, b) => b.onderdelen - a.onderdelen || a.naam.localeCompare(b.naam),
  );
};

export const BuildingRenovationLabels = ({
  projects,
  isLoading = false,
}: BuildingRenovationLabelsProps) => {
  const labels = useMemo(() => telRenovatieLabels(projects), [projects]);

  // Tijdens het laden niets: een lege regel die daarna volloopt springt, en
  // "geen labels" zou dan even te lezen staan terwijl het er wél zijn.
  if (isLoading) return null;

  const onderdelenZonderLabel = (projects ?? []).reduce(
    (som, project) =>
      som +
      (project?.parts ?? []).filter((part) => !(part as any)?.label?.name)
        .length,
    0,
  );

  if (labels.length === 0 && onderdelenZonderLabel === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-neutral-secondary-mid flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
        <Tag size={12} weight="duotone" />
        Renovatielabels
      </span>

      {labels.map(({ naam, onderdelen }) => {
        const { icon, bgColor, textColor } = getRenoCategoryStyle(naam);
        return (
          <span
            key={naam}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${bgColor} ${textColor}`}
            title={`${onderdelen} projectonderdeel${onderdelen === 1 ? "" : "en"} met de categorie ${naam}`}
          >
            {icon}
            {naam}
            {onderdelen > 1 && (
              <span className="opacity-70">({onderdelen})</span>
            )}
          </span>
        );
      })}

      {/* Onderdelen zonder categorie apart benoemen: die zitten in geen enkel
          chipje, en op het renovatie-overzicht vallen ze ook buiten de vier
          hoofdcategorieën. Ze stil weglaten zou de opsomming completer doen
          lijken dan ze is. */}
      {onderdelenZonderLabel > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
          title="Deze projectonderdelen hebben nog geen categorie, en vallen daardoor ook buiten de cijfers per hoofdcategorie"
        >
          {onderdelenZonderLabel} zonder categorie
        </span>
      )}
    </div>
  );
};
